import type {
  DungeonCastResultMessage,
  DungeonRunStatusMessage,
} from './net/protocol';
import { arpgClass } from './sim/arpg/classes';
import {
  ARPG_ABILITY_SLOTS,
  type ArpgAbilitySlot,
} from './sim/arpg/classes';
import type { HeroClassId } from './sim/hero';
import { arpgItemName, arpgItemTooltip } from './sim/arpgItem';
import {
  ability as catalogAbility,
  abilityPresentation,
  type AbilityId,
} from './sim/combat/abilityCatalog';

const SERVER_TICK_SECONDS = 0.05;

/** What one action-bar slot renders. Everything here comes from the catalog. */
interface HudAbility {
  id: AbilityId;
  name: string;
  manaCost: number;
  cooldownTicks: number;
}

/** The key legend each slot draws, which is the binding the client installs. */
const SLOT_KEYS: Readonly<Record<ArpgAbilitySlot, string>> = {
  'left-mouse': 'LMB',
  'right-mouse': 'RMB',
  'key-3': '3',
  'key-4': '4',
  'key-5': '5',
};

/**
 * The ability each slot holds for this class, at this level.
 *
 * Layout is authored on the class declaration and membership comes from the
 * run's own loadout, so the bar is the same five slots for every class and the
 * HUD names no ability id at all (PRD #800).
 */
export function heroSlotAbilities(
  classId: HeroClassId | undefined,
  abilities: readonly AbilityId[] | undefined,
): Partial<Record<ArpgAbilitySlot, AbilityId>> {
  const definition = classId === undefined ? null : arpgClass(classId);
  const loadout = abilities ?? [];
  const slots: Partial<Record<ArpgAbilitySlot, AbilityId>> = {};
  const placed = new Set<AbilityId>();
  for (const entry of definition?.abilities ?? []) {
    if (!loadout.includes(entry.abilityId)) continue;
    slots[entry.slot] = entry.abilityId;
    placed.add(entry.abilityId);
  }
  // An ability the run granted that the class never authored a slot for - an
  // affix grant, or a throwaway sixth entry proving that adding an ability is
  // a catalog entry - takes the next free slot rather than being unplayable.
  const free = ARPG_ABILITY_SLOTS.filter((slot) => slots[slot] === undefined);
  for (const abilityId of loadout) {
    if (placed.has(abilityId)) continue;
    const slot = free.shift();
    if (slot === undefined) break;
    slots[slot] = abilityId;
    placed.add(abilityId);
  }
  return slots;
}

/**
 * Resolve an ability id into the numbers and the name the HUD draws.
 *
 * The wire carries ids alone, so mechanics come from the catalog definition and
 * the player-facing name from the presentation descriptor. This module is the
 * client edge and is therefore one of the layers allowed to read presentation.
 */
function hudAbility(id: AbilityId): HudAbility {
  const definition = catalogAbility(id);
  return {
    id,
    name: abilityPresentation(id)?.name ?? id,
    manaCost: definition?.costs.mana ?? 0,
    cooldownTicks: definition?.cooldownTicks ?? 0,
  };
}

export interface DungeonCombatHudHandle {
  update(msg: DungeonRunStatusMessage): void;
  tryPredictCast(seq: number, abilityId?: string): boolean;
  reconcile(msg: DungeonCastResultMessage): void;
  reset(): void;
}

function element<K extends keyof HTMLElementTagNameMap>(
  doc: Document,
  tag: K,
  id?: string,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = doc.createElement(tag);
  if (id) node.id = id;
  if (className) node.className = className;
  return node;
}

function labelledBar(
  doc: Document,
  root: HTMLElement,
  kind: 'health' | 'mana',
  label: string,
): { fill: HTMLElement; text: HTMLElement } {
  const row = element(doc, 'div', undefined, 'dungeon-vital-row');
  const heading = element(doc, 'span', undefined, 'dungeon-vital-label');
  heading.textContent = label;
  const bar = element(doc, 'div', undefined, `dungeon-vital dungeon-vital-${kind}`);
  bar.setAttribute('role', 'progressbar');
  bar.setAttribute('aria-label', label);
  const fill = element(doc, 'div', `dungeon-${kind}-fill`, 'dungeon-vital-fill');
  const text = element(doc, 'span', `dungeon-${kind}-text`, 'dungeon-vital-text');
  bar.append(fill, text);
  row.append(heading, bar);
  root.append(row);
  return { fill, text };
}

function fraction(value: number, max: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return 0;
  return Math.min(1, Math.max(0, value / max));
}

function setBar(
  fill: HTMLElement,
  text: HTMLElement,
  value: number,
  max: number,
): void {
  fill.style.width = `${fraction(value, max) * 100}%`;
  text.textContent = `${value} / ${max}`;
  const bar = fill.parentElement;
  bar?.setAttribute('aria-valuemin', '0');
  bar?.setAttribute('aria-valuemax', String(max));
  bar?.setAttribute('aria-valuenow', String(value));
}

export function initDungeonCombatHud(
  doc: Document = document,
): DungeonCombatHudHandle {
  const root = element(doc, 'aside', 'dungeon-combat-hud');
  root.hidden = true;
  root.setAttribute('aria-label', 'Dungeon combat');

  const header = element(doc, 'header', undefined, 'dungeon-combat-header');
  const identity = element(doc, 'span', 'dungeon-class');
  const kills = element(doc, 'span', 'dungeon-kills');
  header.append(identity, kills);
  root.append(header);
  const scenarioDescription = element(
    doc,
    'p',
    'dungeon-lab-scenario',
    'dungeon-lab-scenario',
  );
  scenarioDescription.hidden = true;
  root.append(scenarioDescription);

  const vitals = element(doc, 'div', undefined, 'dungeon-vitals');
  const health = labelledBar(doc, vitals, 'health', 'Health');
  const mana = labelledBar(doc, vitals, 'mana', 'Mana');
  root.append(vitals);

  const actionbar = element(doc, 'div', undefined, 'dungeon-actionbar');
  actionbar.setAttribute('aria-label', 'Hero action bar');

  /**
   * One slot, built once and refilled per status.
   *
   * There were five of these written out by hand, each naming its Warrior
   * ability and its own copy of the same cooldown arithmetic. They are one
   * shape now: the layout says which slot exists and the class declaration says
   * what is in it, so a fourth class adds no element here (PRD #800).
   */
  interface HudSlot {
    root: HTMLElement;
    icon: HTMLElement;
    cooldown: HTMLElement;
    name: HTMLElement;
    state: HTMLElement;
  }

  const slots = new Map<ArpgAbilitySlot, HudSlot>();
  for (const slot of ARPG_ABILITY_SLOTS) {
    const root = element(
      doc,
      'div',
      `dungeon-slot-${slot}`,
      'sphere-hotbar-slot dungeon-actionbar-slot',
    );
    const icon = element(doc, 'span', undefined, 'dungeon-skill-icon');
    icon.setAttribute('aria-hidden', 'true');
    const key = element(doc, 'span', undefined, 'sphere-hotbar-key');
    key.textContent = SLOT_KEYS[slot];
    const cooldown = element(doc, 'span', undefined, 'sphere-hotbar-cooldown');
    cooldown.hidden = true;
    cooldown.setAttribute('aria-hidden', 'true');
    const tip = element(doc, 'span', undefined, 'sphere-hotbar-tip');
    const name = element(doc, 'strong', undefined, 'sphere-hotbar-tip-name');
    const state = element(doc, 'span', undefined, 'sphere-hotbar-tip-cost');
    tip.append(name, state);
    root.append(icon, key, cooldown, tip);
    actionbar.append(root);
    slots.set(slot, { root, icon, cooldown, name, state });
  }

  for (let index = ARPG_ABILITY_SLOTS.length; index < 8; index++) {
    const empty = element(
      doc,
      'span',
      undefined,
      'sphere-hotbar-slot dungeon-actionbar-slot dungeon-actionbar-slot--empty',
    );
    empty.setAttribute('aria-hidden', 'true');
    actionbar.append(empty);
  }
  root.append(actionbar);

  const backpack = element(doc, 'section', 'dungeon-backpack');
  const backpackTitle = element(doc, 'strong');
  backpackTitle.textContent = 'Run backpack';
  const backpackContents = element(doc, 'span', 'dungeon-backpack-contents');
  backpack.append(backpackTitle, backpackContents);
  root.append(backpack);

  doc.body.append(root);

  let current: DungeonRunStatusMessage | undefined;
  let predictedSeq: number | undefined;

  const render = (): void => {
    if (!current?.active) {
      root.hidden = true;
      return;
    }
    root.hidden = false;
    const scenarioLabel =
      current.mode === 'combat-lab'
        ? `Combat Lab${current.labScenario ? `: ${current.labScenario.label}` : ''}  •  `
        : '';
    const className = arpgClass(current.classId)?.name ?? current.classId;
    identity.textContent =
      scenarioLabel + `${className}  •  Level ${current.level}  •  ${current.xp} XP`;
    scenarioDescription.hidden =
      current.mode !== 'combat-lab' || current.labScenario === undefined;
    scenarioDescription.textContent = current.labScenario?.description ?? '';
    kills.textContent = `${current.kills} ${current.kills === 1 ? 'kill' : 'kills'}`;
    setBar(health.fill, health.text, current.hp, current.maxHp);
    setBar(mana.fill, mana.text, current.mana, current.maxMana);

    const bound = heroSlotAbilities(current.classId, current.abilities ?? [current.ability]);
    for (const [slot, hud] of slots) {
      const abilityId = bound[slot];
      // A slot with nothing in it stays on the bar as an empty socket rather
      // than collapsing it: a kit that unlocks across a campaign should show
      // the player what it is going to fill, and a bar that changes width as
      // abilities unlock moves every other slot under their fingers.
      hud.root.classList.toggle(
        'dungeon-actionbar-slot--empty',
        abilityId === undefined,
      );
      if (abilityId === undefined) {
        hud.icon.className = '';
        hud.name.textContent = '';
        hud.state.textContent = '';
        hud.cooldown.hidden = true;
        delete hud.root.dataset.ready;
        continue;
      }
      const ability = hudAbility(abilityId);
      const remaining = current.cooldowns[ability.id] ?? 0;
      const affordable = current.mana >= ability.manaCost;
      hud.icon.className = `dungeon-skill-icon dungeon-icon-${ability.id}`;
      hud.name.textContent = ability.name;
      hud.state.textContent =
        remaining > 0
          ? `${(remaining * SERVER_TICK_SECONDS).toFixed(1)}s`
          : !affordable
            ? `Need ${ability.manaCost} mana`
            : ability.manaCost === 0
              ? 'Ready'
              : `Ready  •  ${ability.manaCost} mana`;
      hud.root.dataset.ready = remaining === 0 && affordable ? 'true' : 'false';
      hud.cooldown.hidden = remaining === 0;
      hud.cooldown.textContent =
        remaining === 0 ? '' : (remaining * SERVER_TICK_SECONDS).toFixed(1);
      hud.cooldown.style.setProperty(
        '--sphere-cooldown-angle',
        `${(1 - fraction(remaining, ability.cooldownTicks)) * 360}deg`,
      );
    }

    // The run backpack: what is riding on the extraction, by name and rarity.
    // It is the same rolled name the ground label and the stash row read, so a
    // player never has to work out which drop a row is.
    backpackContents.replaceChildren();
    for (const [index, item] of current.backpack.entries()) {
      const entry = doc.createElement('span');
      entry.className = `dungeon-backpack-item dungeon-item-${item.rarity}`;
      entry.textContent = index === 0 ? arpgItemName(item) : `, ${arpgItemName(item)}`;
      const tooltip = arpgItemTooltip(item);
      entry.title = [
        `${tooltip.name} (${tooltip.rarity})`,
        ...tooltip.lines.map((line) => `${line.label} ${line.value}`),
      ].join('\n');
      backpackContents.appendChild(entry);
    }
    if (current.backpack.length === 0) backpackContents.textContent = 'Empty';
    backpack.hidden = current.backpack.length === 0;
  };

  return {
    update(msg) {
      const prediction =
        predictedSeq !== undefined && current?.active
          ? { mana: current.mana, cooldowns: current.cooldowns }
          : undefined;
      current = {
        ...msg,
        cooldowns: { ...msg.cooldowns },
        ...(msg.abilities ? { abilities: [...msg.abilities] } : {}),
      };
      if (prediction && msg.active) {
        current.mana = prediction.mana;
        current.cooldowns = prediction.cooldowns;
      } else {
        predictedSeq = undefined;
      }
      render();
    },
    tryPredictCast(seq, abilityId = current?.ability) {
      const status = current;
      const known =
        abilityId !== undefined &&
        (status?.abilities?.includes(abilityId) || status?.ability === abilityId);
      const ability = known && abilityId ? hudAbility(abilityId) : undefined;
      if (
        !status?.active ||
        !ability ||
        (status.cooldowns[ability.id] ?? 0) > 0 ||
        status.mana < ability.manaCost
      ) {
        return false;
      }
      status.mana -= ability.manaCost;
      // Per-ability cooldowns, so predicting one ability never greys out another.
      status.cooldowns = {
        ...status.cooldowns,
        [ability.id]: ability.cooldownTicks,
      };
      predictedSeq = seq;
      render();
      return true;
    },
    reconcile(msg) {
      if (!current?.active) return;
      if (predictedSeq !== undefined && msg.seq !== predictedSeq) return;
      current.mana = msg.mana;
      current.cooldowns = { ...msg.cooldowns };
      predictedSeq = undefined;
      render();
    },
    reset() {
      current = undefined;
      predictedSeq = undefined;
      root.hidden = true;
    },
  };
}
